<script>
  import { onMount, afterUpdate, createEventDispatcher } from 'svelte';
  
  export let filteredProjects = [];
  export let displayedProjects = [];
  export let searchTerm = '';
  export let sortOption = '최신순';
  export let hasMoreItems = true;
  export let isLoadingMore = false;
  export let intersectionTarget;
  export let onSortChange = () => {};
  export let onViewProject = () => {};
  export let onSearch = () => {};
  export let isAdmin = false;

  const dispatch = createEventDispatcher();

  function handleSortChange(option) {
    sortOption = option;
    onSortChange(option);
  }


  // 텍스트 오버플로우 확인 및 툴팁 설정
  function checkTextOverflow() {
    // 현재 표시된 프로젝트 카드들의 info-value만 대상으로 함
    const projectCards = document.querySelectorAll('.project-card');
    projectCards.forEach(card => {
      const infoValues = card.querySelectorAll('.info-value');
      infoValues.forEach(element => {
        // 요소가 실제로 렌더링된 후 확인하기 위해 약간의 지연
        requestAnimationFrame(() => {
          if (element.scrollWidth > element.clientWidth) {
            // 텍스트가 오버플로우되는 경우에만 title 속성 추가
            element.setAttribute('title', element.textContent.trim());
            element.style.cursor = 'help';
          } else {
            // 오버플로우되지 않는 경우 title 속성 제거
            element.removeAttribute('title');
            element.style.cursor = 'default';
          }
        });
      });
    });
  }

  // 컴포넌트 마운트 후 및 업데이트 후 오버플로우 체크
  afterUpdate(() => {
    checkTextOverflow();
  });
</script>

<section class="project-list-section">
  <div class="list-header">
    <div class="total-count">
      <strong>총 {filteredProjects.length}</strong>건의 프로젝트
      {#if displayedProjects.length < filteredProjects.length}
        <span class="loading-progress">({displayedProjects.length}/{filteredProjects.length} 표시됨)</span>
      {/if}
    </div>
    <div class="sort-options">
      <select bind:value={sortOption} on:change={() => handleSortChange(sortOption)}>
        <option>최신순</option>
        <option>마감임박순</option>
        <option>마감순</option>
      </select>
    </div>
  </div>
  
  <div class="project-list">
    {#if filteredProjects.length === 0 && searchTerm.trim() !== ''}
      <div class="empty-container">
        <div class="empty-icon">🔍</div>
        <div class="empty-text">"{searchTerm}"에 대한 검색 결과가 없습니다.</div>
        <button class="retry-btn" on:click={() => { searchTerm = ''; onSearch(); }}>전체 프로젝트 보기</button>
      </div>
    {:else if filteredProjects.length === 0}
      <div class="empty-container">
        <div class="empty-icon">📋</div>
        <div class="empty-text">등록된 프로젝트가 없습니다.</div>
      </div>
    {:else}
      <!-- 프로젝트 카드들 -->
      {#each displayedProjects as project (project.id)}
        <div 
          class="project-card" 
          on:click={() => onViewProject(project)}
          on:keydown={(e) => e.key === 'Enter' && onViewProject(project)}
          tabindex="0"
          role="button"
        >
          <div class="card-header">
            <div class="project-category {project.type === '외주' ? 'outsourcing' : ''}">{project.type}</div>
            <div class="project-company">{project.companyName}</div>
          </div>
          <h3 class="project-title">
            {project.title}
            <span class="status-badge {project.hasApplied ? 'applied' : project.status}">
              {#if isAdmin}
                지원 {project.applicantCount || 0}건
              {:else}
                {project.hasApplied ? '지원완료' : project.status}
              {/if}
            </span>
          </h3>
          <div class="project-info-grid">
            <div class="info-row">
              <div class="info-label">근무형태</div>
              <div class="info-value">{project.workType}</div>
            </div>
            <div class="info-row">
              <div class="info-label">근무위치</div>
              <div class="info-value">{project.location || '협의가능'}</div>
            </div>
            <div class="info-row">
              <div class="info-label">필요인원</div>
              <div class="info-value">{project.headcount || '미정'}</div>
            </div>
            <div class="info-row">
              <div class="info-label">경력</div>
              <div class="info-value">{project.level || '무관'}</div>
            </div>
          </div>
          <div class="project-skills">
            {#each project.skills.slice(0, 3) as skill}
              <span class="skill-tag">{skill}</span>
            {/each}
            {#if project.skills.length > 3}
              <span class="skill-tag">+{project.skills.length - 3}</span>
            {/if}
          </div>
          <div class="project-footer">
            <div class="status-button-container">
              {#if project.status === '마감' || project.deadline === '마감'}
                <button class="status-button closed" disabled>마감</button>
              {:else}
                <button class="status-button recruiting" disabled>모집중</button>
                <button class="deadline-button" disabled>
                  {project.deadline || '상시채용'}
                </button>
              {/if}
            </div>
          </div>
        </div>
      {/each}
      
    {/if}
    
    <!-- 무한 스크롤 트리거 요소 (항상 표시) -->
    <div 
      bind:this={intersectionTarget} 
      class="infinite-scroll-trigger"
      style="height: 20px; width: 100%; grid-column: 1 / -1; background: transparent; margin: 10px 0;"
    ></div>
  </div>
  
  <!-- 로딩 인디케이터 -->
  {#if isLoadingMore}
    <div class="loading-indicator">
      <div class="loading-spinner"></div>
      <span>더 많은 프로젝트를 불러오는 중...</span>
    </div>
  {/if}
  
  <!-- 모든 항목 로드 완료 메시지 -->
  {#if displayedProjects.length > 0 && !hasMoreItems && !isLoadingMore}
    <div class="end-message">
      <div class="end-icon">✓</div>
      <div>모든 프로젝트를 불러왔습니다.</div>
      <div class="end-count">총 {displayedProjects.length}개 프로젝트</div>
    </div>
  {/if}
</section>

<style>
  /* ======================
     프로젝트 목록 섹션 (Global Styles)
     ====================== */
  :global(.main-content .project-list-section) {
    flex: 1;
    min-width: 0;
    width: 100%;
    padding: 0px;
  }
  
  /* ======================
     프로젝트 목록 섹션 (Local Styles)
     ====================== */
  .project-list-section {
    flex: 1;
    min-width: 0;
    width: 100%;
    padding: 0px;
  }

  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 0 10px;
    gap: 20px;
  }

  .total-count {
    font-size: 1.1rem;
    color: #333;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .loading-progress {
    font-size: 0.9rem;
    color: #666;
    font-weight: normal;
  }

  .sort-options select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    color: #333;
    background-color: white;
  }

  .project-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 20px;
    width: 100%;
    align-items: start;
    justify-items: stretch;
    overflow: hidden;
    max-height: none;
  }

  /* 프로젝트 카드 */
  .project-card {
    background-color: white;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s, box-shadow 0.2s;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    border: 1px solid #e9ecef;
    height: auto;
    min-height: 280px;
    margin-bottom: 10px;
    align-self: start;
  }

  .project-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    background-color: #fff;
    border-bottom: 1px solid #e9ecef;
  }

  .project-category {
    background-color: #4183f3;
    color: white;
    padding: 5px 12px;
    border-radius: 4px;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .project-category.outsourcing {
    background-color: #28a745;
    color: white;
  }

  .project-company {
    color: #495057;
    font-size: 0.9rem;
    text-align: right;
  }

  .project-title {
    padding: 8px 12px;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #e9ecef;
    min-height: auto;
  }

  .project-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    padding: 0;
    gap: 5px;
    margin-bottom: 5px;
  }

  .info-row {
    padding: 2px 8px;
    border-bottom: none;
  }

  .info-label {
    color: #868e96;
    font-size: 0.8rem;
    margin-bottom: 0;
    display: block;
  }

  .info-value {
    color: #333;
    font-weight: 500;
    font-size: 0.85rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    display: block;
    cursor: default;
  }

  .project-skills {
    padding: 6px 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    border-top: none;
  }

  .skill-tag {
    background-color: #f1f3f5;
    color: #495057;
    padding: 3px 8px;
    border-radius: 15px;
    font-size: 0.75rem;
  }

  .project-footer {
    padding: 12px 16px;
    text-align: right;
    border-top: 1px solid #e9ecef;
  }

  .status-button-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
  }

  .status-button {
    background: none;
    border: 2px solid;
    padding: 8px 16px;
    font-size: 0.9rem;
    font-weight: 600;
    border-radius: 4px;
    cursor: default;
    transition: none;
  }

  .status-button.recruiting {
    border-color: #868e96;
    color: #868e96;
    background-color: #fff;
  }

  .status-button.closed {
    border-color: #868e96;
    color: #868e96;
    background-color: #f8f9fa;
  }

  .deadline-button {
    background: none;
    border: 2px solid #40c057;
    color: #40c057;
    padding: 8px 16px;
    font-size: 0.85rem;
    font-weight: 600;
    border-radius: 4px;
    cursor: default;
    transition: none;
    white-space: nowrap;
  }

  .deadline-info {
    color: #fa5252;
    font-size: 0.85rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .project-deadline {
    color: #fa5252;
    font-size: 0.8rem;
    font-weight: 500;
  }

  /* 상태 뱃지 */
  .status-badge {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 12px;
    margin-left: 8px;
  }

  .status-badge.모집중 {
    background-color: #e7f5ff;
    color: #3273dc;
    border: 1px solid #3273dc;
  }

  .status-badge.마감 {
    background-color: #f1f3f5;
    color: #495057;
    border: 1px solid #adb5bd;
  }

  .status-badge.applied {
    background-color: #fff4e6;
    color: #f59f00;
    border: 1px solid #f59f00;
  }

  .status-badge.reviewing {
    background-color: #fff9db;
    color: #f59f00;
  }

  .status-badge.proceeding {
    background-color: #d3f9d8;
    color: #40c057;
  }

  .status-badge.accepted {
    background-color: #b2f2bb;
    color: #2b8a3e;
  }

  .status-badge.rejected {
    background-color: #ffe3e3;
    color: #e03131;
  }

  .status-badge.project_canceled,
  .status-badge.canceled {
    background-color: #ffe3e3;
    color: #fa5252;
  }

  .status-badge.project_delayed {
    background-color: #e5dbff;
    color: #7950f2;
  }

  .status-badge.closed {
    background-color: #f1f3f5;
    color: #868e96;
  }

  /* 무한 스크롤 트리거 */
  .infinite-scroll-trigger {
    visibility: hidden;
    pointer-events: none;
  }

  /* 로딩 인디케이터 */
  .loading-indicator {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    padding: 30px 20px;
    color: #666;
    font-size: 1rem;
    background-color: #fff;
    border-radius: 8px;
    margin: 20px 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .loading-spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #3273dc;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* 종료 메시지 */
  .end-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    padding: 30px 20px;
    color: #666;
    font-size: 0.95rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    margin: 20px 0;
    border: 1px solid #e9ecef;
  }

  .end-icon {
    font-size: 1.5rem;
    color: #28a745;
    margin-bottom: 5px;
  }

  .end-count {
    font-size: 0.85rem;
    color: #868e96;
    font-weight: 500;
  }

  /* 빈 상태 */
  .empty-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
    grid-column: 1 / -1;
    text-align: center;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    margin: 10px 0;
  }

  .empty-icon {
    font-size: 3.5rem;
    margin-bottom: 15px;
    color: #ced4da;
  }

  .retry-btn {
    background-color: #3273dc;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    cursor: pointer;
    font-size: 0.9rem;
    margin-top: 5px;
  }

  .retry-btn:hover {
    background-color: #2860b5;
  }

  @media (max-width: 768px) {
    .project-list {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    }

    .project-info-grid {
      grid-template-columns: 1fr;
    }
  }

  /* 지원 완료 상태 버튼 */
  .status-button.applied {
    border-color: #f59f00;
    color: #f59f00;
    background-color: #fff;
  }
</style>